import { Button } from '@eventbrite/eds-button';
import { isSmallDown } from '@eventbrite/eds-utils';
import { setWindowLocation } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import includes from 'lodash/includes';
import React, { Component } from 'react';
import { formatUrl } from 'url-lib';
import { setAction } from '../../utils';
import './onboardingEntry.scss';

const ONBOARDING_PATH = '/u/interests/';

const IMG_MAP: { [key: string]: string } = {
    mobile:
        'https://cdn.evbstatic.com/s3-build/perm_001/f8aabe/django/images/onboarding/mobile_entry.jpg',
    standard:
        'https://cdn.evbstatic.com/s3-build/perm_001/9c951d/django/images/onboarding/desktop_entry.jpg',
    standardPurple:
        'https://cdn.evbstatic.com/s3-build/perm_001/ad8d44/django/images/onboarding/desktop_entry_2.jpg',
    wide:
        'https://cdn.evbstatic.com/s3-build/perm_001/c7e0b0/django/images/onboarding/desktop_entry_wide.jpg',
    confirmation:
        'https://cdn.evbstatic.com/s3-build/perm_001/7f5101/django/images/onboarding/confirmation_wide.jpg',
};

interface OnboardingEntryProps {
    isAuthenticated?: boolean;
    onButtonClick?: Function;
    isMobile?: boolean;
    type?: 'wide' | 'standard' | 'standardPurple';
    referrer?: string;
    containerClasses?: string;
}

interface OnboardingEntryState {
    isMobileEntry?: Function;
}

export class OnboardingEntry extends Component<
    OnboardingEntryProps,
    OnboardingEntryState
> {
    constructor(props: Readonly<OnboardingEntryProps>) {
        super(props);

        this.state = {
            isMobileEntry: isSmallDown() || props.isMobile,
        };
    }

    static defaultProps = {
        type: 'standard',
        referrer: '/',
    };

    _getImgSrc = () => {
        const { type } = this.props;
        const { isMobileEntry } = this.state;

        if (isMobileEntry) {
            return IMG_MAP.mobile;
        }

        return IMG_MAP[type || ''];
    };

    _getHref = () => {
        const { isAuthenticated, referrer } = this.props;

        let href = formatUrl(ONBOARDING_PATH, {
            onboarding_referrer: referrer || null,
        });

        if (!isAuthenticated) {
            href = `/signin/confirmation/?referrer=/u/interests/?onboarding_referrer=${referrer}`;
        }

        return href;
    };

    _handleButtonClick = () => {
        const { onButtonClick, isAuthenticated } = this.props;

        if (!isAuthenticated) {
            this._setLoggedOutActionCookie();
        }

        onButtonClick?.();

        setWindowLocation(this._getHref());
    };

    _setLoggedOutActionCookie = () => {
        setAction({
            action: 'consumerOnboarding',
            yPos: 0,
            payload: {
                image: IMG_MAP.confirmation,
                title: '',
            },
        });
    };

    render() {
        const { containerClasses, type } = this.props;

        const { isMobileEntry } = this.state;

        const entryPointContainerClasses = classNames(
            'onboarding-entry__container',
            {
                [containerClasses || '']: containerClasses,
                'onboarding-entry--standard':
                    includes(type, 'standard') && !isMobileEntry,
                'onboarding-entry--wide': type === 'wide' && !isMobileEntry,
                'onboarding-entry--mobile': isMobileEntry,
            },
        );

        const headerClasses = classNames(
            'onboarding-entry__content__text--header',
            {
                'eds-text-hs': isMobileEntry,
                'eds-text-hm': !isMobileEntry,
            },
        );

        const byLineClasses = classNames(
            'eds-l-pad-bot-2',
            'onboarding-entry__content__text--byline',
            {
                'eds-hide-sm': includes(type, 'standard'),
                'eds-text-bm': !isMobileEntry,
                'eds-text-bs': isMobileEntry,
            },
        );

        return (
            <div className={entryPointContainerClasses}>
                <section className="onboarding-entry">
                    <section className="onboarding-entry__content eds-l-pad-right-4">
                        <div className="onboarding-entry__content__text">
                            <h4 className={headerClasses}>
                                {gettext('Tell us what you love')}
                            </h4>
                            <div className={byLineClasses}>
                                {gettext(
                                    "We'll find event recomendations just for you",
                                )}
                            </div>
                        </div>
                        <Button
                            type="link"
                            style="fill"
                            onClick={this._handleButtonClick}
                            href={this._getHref()}
                            __containerClassName="onboarding-entry__button"
                        >
                            {gettext('Get started')}
                        </Button>
                    </section>
                    <img
                        alt={gettext(
                            'Personalize your event discovery experience',
                        ).toString()}
                        className="onboarding-entry__img"
                        src={this._getImgSrc()}
                    />
                </section>
            </div>
        );
    }
}

export default OnboardingEntry;
